import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import DownloadIcon from "@redotech/redo-icons/download-01.svg";
import UploadCloudIcon from "@redotech/redo-icons/upload-cloud-02.svg";
import { Provider } from "@redotech/redo-model/order";
import { toast } from "@redotech/redo-web/alert";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoCheckbox } from "@redotech/redo-web/arbiter-components/checkbox/redo-checkbox";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Card } from "@redotech/redo-web/card";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useMemo, useState } from "react";
import { RedoAdminRpcClientContext } from "../../../app/redo-admin-rpc-client-provider";

const CSV_COLUMNS = {
  PACKAGE_LENGTH: "Package length",
  PACKAGE_WIDTH: "Package width",
  PACKAGE_HEIGHT: "Package height",
  ALL_SKUS: "All SKUs",
  BOX: "Box",
} as const;

function generateExampleCsv(): string {
  const headers = Object.values(CSV_COLUMNS);
  const exampleRows = [
    ["12", "10", "8", "SKU-001*2,SKU-002", "Small Box"],
    ["18", "14", "10", "SKU-003,SKU-004*3,SKU-005", "Medium Box"],
    ["24", "18", "12", "SKU-006", "Large Box"],
  ];

  const rows = [headers, ...exampleRows];
  return rows.map((row) => row.map((cell) => `"${cell}"`).join(",")).join("\n");
}

function downloadCsv(content: string, filename: string): void {
  const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);

  link.setAttribute("href", url);
  link.setAttribute("download", filename);
  link.style.visibility = "hidden";

  document.body.append(link);
  link.click();
  link.remove();

  URL.revokeObjectURL(url);
}

const PROVIDER_OPTIONS: { value: Provider; label: string }[] = [
  { value: Provider.SHOPIFY, label: "Shopify" },
  { value: Provider.BIGCOMMERCE, label: "BigCommerce" },
];

export const ImportPackageLearningCsvCard = memo(
  function ImportPackageLearningCsvCard({
    teamId,
  }: {
    teamId: string | undefined;
  }) {
    const rpcClient = useRequiredContext(RedoAdminRpcClientContext);
    const [file, setFile] = useState<File | null>(null);
    const [shipmentsFile, setShipmentsFile] = useState<File | null>(null);
    const [itemsFile, setItemsFile] = useState<File | null>(null);
    const [loading, setLoading] = useState(false);
    const [provider, setProvider] = useState<Provider>(Provider.SHOPIFY);
    const [useShipStationFormat, setUseShipStationFormat] = useState(false);

    const providerOptions: RedoListItem<Provider>[] = useMemo(
      () =>
        PROVIDER_OPTIONS.map((opt) => ({
          id: opt.value,
          type: "text" as const,
          text: opt.label,
          value: opt.value,
        })),
      [],
    );

    const selectedProviderOption = useMemo(
      () => providerOptions.find((opt) => opt.value === provider),
      [providerOptions, provider],
    );

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files;
      if (files && files[0]) {
        setFile(files[0]);
      }
    };

    const handleShipmentsFileChange = (
      event: React.ChangeEvent<HTMLInputElement>,
    ) => {
      const files = event.target.files;
      if (files && files[0]) {
        setShipmentsFile(files[0]);
      }
    };

    const handleItemsFileChange = (
      event: React.ChangeEvent<HTMLInputElement>,
    ) => {
      const files = event.target.files;
      if (files && files[0]) {
        setItemsFile(files[0]);
      }
    };

    const handleDownloadExample = useHandler(() => {
      const csvContent = generateExampleCsv();
      downloadCsv(csvContent, "package-learning-example.csv");
    });

    const handleSubmit = useHandler(async () => {
      if (!teamId || !rpcClient) {
        return;
      }

      if (useShipStationFormat && (!shipmentsFile || !itemsFile)) {
        toast("Please select both shipments and items CSV files", {
          variant: "error",
        });
        return;
      }

      if (!useShipStationFormat && !file) {
        toast("Please select a CSV file", { variant: "error" });
        return;
      }

      try {
        setLoading(true);

        const result = useShipStationFormat
          ? await rpcClient.importShipStationPackageLearning(
              { teamId, provider },
              { files: [shipmentsFile!, itemsFile!] },
            )
          : await rpcClient.importPackageLearningCsv(
              { teamId, provider },
              { files: [file!] },
            );

        if (result.errors.length > 0) {
          toast(
            `Import completed with warnings: ${result.packagesCreated} packages created, ${result.packagesUpdated} updated, ${result.learningPresetsApplied} learning presets applied. Warnings: ${result.errors.join(", ")}`,
            { variant: "warning" },
          );
        } else {
          toast(
            `Package learning import successful: ${result.packagesCreated} packages created, ${result.packagesUpdated} updated, ${result.learningPresetsApplied} learning presets applied`,
            { variant: "success" },
          );
        }

        // Clear files
        setFile(null);
        setShipmentsFile(null);
        setItemsFile(null);
      } catch (error) {
        toast("Failed to import package learning CSV", { variant: "error" });
        console.error("Failed to import package learning CSV:", error);
      } finally {
        setLoading(false);
      }
    });

    return (
      <Card title="Package Learning CSV Import">
        <Flex dir="column" gap="lg">
          {!useShipStationFormat && (
            <Flex align="center" gap="md">
              <RedoButton
                hierarchy="secondary"
                IconLeading={DownloadIcon}
                onClick={handleDownloadExample}
                size="sm"
                text="Download Example CSV"
                theme="normal"
              />
            </Flex>
          )}

          <Flex dir="column" gap="sm">
            <Text fontSize="sm" fontWeight="medium">
              Provider
            </Text>
            <RedoSingleSelectDropdownInput
              options={providerOptions}
              optionSelected={({ item }) => {
                if (item && typeof item !== "string" && item.value) {
                  setProvider(item.value);
                }
              }}
              selectedItem={selectedProviderOption}
              size="sm"
            />
          </Flex>

          <Flex align="center" gap="sm">
            <RedoCheckbox
              setValue={(checked) => {
                setUseShipStationFormat(checked === true);
                // Clear files when switching modes
                setFile(null);
                setShipmentsFile(null);
                setItemsFile(null);
              }}
              size="sm"
              value={useShipStationFormat}
            />
            <Text fontSize="sm" fontWeight="medium">
              Use ShipStation format (2 files)
            </Text>
          </Flex>

          {useShipStationFormat ? (
            <Flex dir="column" gap="md">
              <Flex align="center" gap="md">
                <label htmlFor="shipments-csv-upload">
                  <RedoButton
                    disabled={loading}
                    hierarchy="primary"
                    IconLeading={UploadCloudIcon}
                    onClick={() => {
                      document.getElementById("shipments-csv-upload")?.click();
                    }}
                    size="md"
                    text="Select Shipments CSV"
                    theme="normal"
                  />
                </label>
                {shipmentsFile && (
                  <Text fontSize="sm" textColor="secondary">
                    {shipmentsFile.name}
                  </Text>
                )}
                <input
                  accept=".csv"
                  id="shipments-csv-upload"
                  onChange={handleShipmentsFileChange}
                  style={{ display: "none" }}
                  type="file"
                />
              </Flex>

              <Flex align="center" gap="md">
                <label htmlFor="items-csv-upload">
                  <RedoButton
                    disabled={loading}
                    hierarchy="primary"
                    IconLeading={UploadCloudIcon}
                    onClick={() => {
                      document.getElementById("items-csv-upload")?.click();
                    }}
                    size="md"
                    text="Select Items CSV"
                    theme="normal"
                  />
                </label>
                {itemsFile && (
                  <Text fontSize="sm" textColor="secondary">
                    {itemsFile.name}
                  </Text>
                )}
                <input
                  accept=".csv"
                  id="items-csv-upload"
                  onChange={handleItemsFileChange}
                  style={{ display: "none" }}
                  type="file"
                />
              </Flex>
            </Flex>
          ) : (
            <Flex align="center" gap="md">
              <label
                htmlFor="package-learning-csv-upload"
                id="package-learning-csv-upload-label"
              >
                <RedoButton
                  disabled={loading}
                  hierarchy="primary"
                  IconLeading={UploadCloudIcon}
                  onClick={() => {
                    document
                      .getElementById("package-learning-csv-upload")
                      ?.click();
                  }}
                  size="md"
                  text="Select File"
                  theme="normal"
                />
              </label>
              {file && (
                <Text fontSize="sm" textColor="secondary">
                  {file.name}
                </Text>
              )}
              <input
                accept=".csv"
                id="package-learning-csv-upload"
                onChange={handleFileChange}
                style={{ display: "none" }}
                type="file"
              />
            </Flex>
          )}

          <RedoButton
            disabled={
              useShipStationFormat
                ? !shipmentsFile || !itemsFile || loading
                : !file || loading
            }
            hierarchy="primary"
            onClick={handleSubmit}
            pending={loading}
            size="md"
            text={loading ? "Importing..." : "Import CSV"}
            theme="normal"
          />
        </Flex>
      </Card>
    );
  },
);
